import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import { getTrustedMobileRedirect, redirectToMobileHandoff } from '../utils/mobileRedirect';
import PasswordInput from '../components/PasswordInput';
import ProfilePhotoInput from '../components/ProfilePhotoInput';

const Page = styled.div`
  background: #0a0a0a;
  background-image: linear-gradient(180deg, rgba(215, 196, 173, 0.2) 0%, rgba(10, 10, 10, 1) 65%);
  min-height: 100vh;
  padding: 2.5rem 1.5rem 3rem;
`;

const Wrapper = styled.div`
  max-width: 420px;
  margin: 2rem auto;
  background: var(--surface);
  color: var(--onSurface);
  padding: 1.75rem;
  border-radius: 16px;

  box-shadow: 0 22px 40px rgba(0, 0, 0, 0.35);
  overflow: hidden;
`;

const Hero = styled.div`
  
  border-radius: 14px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  color: var(--onSurface);
`;

const Logo = styled.img`
  width: 52px;
  height: 52px;
  object-fit: contain;
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const HeroTitle = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  color: var(--onSurface);
`;

const HeroSubtitle = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.95rem;
`;

const Stepper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin: 1rem 0 1.5rem;
`;

const StepItem = styled.div`
  flex: 1;
  text-align: center;
  font-size: 0.75rem;
  color: var(--onSurfaceVariant);
`;

const StepCircle = styled.div<{ $active?: boolean; $complete?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  margin: 0 auto 0.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${({ $active, $complete }) => ($active || $complete ? 'var(--primary)' : '#1f2937')};
  color: ${({ $active, $complete }) => ($active || $complete ? '#0a0a0a' : '#cbd5f5')};
`;

const StepTitle = styled.h3`
  margin: 0 0 0.35rem;
  font-size: 1.1rem;
  color: #f8fafc;
`;

const StepDescription = styled.p`
  margin: 0 0 1rem;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
`;

const Field = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.25rem;
  color: #e2e8f0;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background:rgb(255, 255, 255);
  color:rgb(0, 0, 0);
  box-sizing: border-box;

  &::placeholder {
    color: var(--onSurfaceVariant);
  }
`;

const Button = styled.button`
  width: 100%;
  background: var(--primary);
  color: var(--onSurface);
  padding: 0.75rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
`;

const SecondaryButton = styled.button`
  width: 100%;
  background: #1f2937;
  color: #e2e8f0;
  padding: 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
`;

const ActionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const ErrorMsg = styled.p`
  color: var(--error);
  margin-top: 0.5rem;
`;

const SuggestionPanel = styled.div`
  margin-top: 0.5rem;
  border: 1px solid #1f2937;
  border-radius: 12px;
  padding: 0.75rem;
  background: #0f172a;
`;

const SuggestionHeader = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const SuggestionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;
  border-top: 1px solid #1f2937;
`;

const SuggestionMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  color: #e2e8f0;
`;

const SuggestionHint = styled.div`
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const RegisteredNotice = styled(SuggestionHint)`
  color: var(--error);
  font-weight: 500;
`;

const ClaimButton = styled.button`
  background: var(--primary);
  color: var(--onSurface);
  border: none;
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
  cursor: pointer;
`;

const PartnerPopup = styled.div`
  margin: 1rem 0 1.5rem;
  border-radius: 16px;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary) 100%);
  color: var(--onSurface);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.35);
`;

const PartnerPopupTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
`;

const PartnerPopupText = styled.div`
  margin-top: 0.4rem;
  font-size: 0.95rem;
  line-height: 1.4;
  color: rgba(10, 10, 10, 0.78);
`;

const PartnerPopupRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.8rem;
`;

const PartnerPopupButton = styled.button`
  background: #0a0a0a;
  color: var(--primary);
  border: none;
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-weight: 600;
  cursor: pointer;
`;

const ToastRegion = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  pointer-events: none;
`;

const Toast = styled.div`
  background: #111827;
  color: #fff;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.35);
  font-size: 0.95rem;
  max-width: 320px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(3, 7, 18, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  padding: 1.5rem;
`;

const ModalCard = styled.div`
  width: min(720px, 92vw);
  background: #0b0f17;
  color: #f8fafc;
  border-radius: 18px;
  border: 1px solid #1f2937;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.45);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #1f2937;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.15rem;
`;

const ModalCloseButton = styled.button`
  background: transparent;
  color: var(--onSurfaceVariant);
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 1rem 1.5rem 1.5rem;
`;

const VideoFrame = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #111827;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #1f2937;

  iframe,
  video {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
`;

const calculateAge = (dateOfBirth: string): number | null => {
  const parsed = new Date(dateOfBirth);
  if (Number.isNaN(parsed.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  const monthDiff = today.getMonth() - parsed.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parsed.getDate())) {
    age -= 1;
  }
  return age;
};

const updateUserProfileWithFallback = async (
  userId: string,
  payload: Record<string, string | null>,
) => {
  const { error } = await supabase.from('users').update(payload).eq('id', userId);
  if (!error) return;

  const message = error.message || '';
  if (!message.toLowerCase().includes('account_kind')) {
    throw new Error(message);
  }

  const { account_kind: _ignored, ...withoutAccountKind } = payload;
  const { error: retryError } = await supabase.from('users').update(withoutAccountKind).eq('id', userId);
  if (retryError) {
    throw new Error(retryError.message);
  }
};

type PhoneMatch = {
  id: string;
  full_name?: string | null;
  phone?: string | null;
};

type ExactMatch = {
  id: string;
  full_name?: string | null;
  phone?: string | null;
  membership_status?: string | null;
};

type SignUpVariant = 'default' | 'partner';

type SignUpCopy = {
  heroTitle: string;
  heroSubtitle: string;
  stepperLabel: string;
  partnerPopupTitle: string;
  partnerPopupText: string;
  submitLabel: string;
  videoTitle?: string;
  videoUrl?: string;
};

const waitForActiveSession = async (
  initialSession: Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'],
  options?: {
    attempts?: number;
    delayMs?: number;
  }
) => {
  if (initialSession?.refresh_token) {
    return initialSession;
  }

  const attempts = options?.attempts ?? 5;
  const delayMs = options?.delayMs ?? 200;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const { data } = await supabase.auth.getSession();
    if (data.session?.refresh_token) {
      return data.session;
    }
    if (attempt < attempts - 1) {
      await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    }
  }

  return null;
};

const defaultCopy: SignUpCopy = {
  heroTitle: 'Sign up',
  heroSubtitle: 'Welcome to New Church of Faith',
  stepperLabel: 'Sign up steps',
  partnerPopupTitle: 'Heads UP!',
  partnerPopupText:
    "We may already have your account. Input your phone number and claim your account before signing up. If there's no match, continue signing up.",
  submitLabel: 'Sign Up',
};

const partnerCopy: SignUpCopy = {
  heroTitle: 'Partner Sign Up',
  heroSubtitle: 'Join the NCOF Family!',
  stepperLabel: 'Partner sign up steps',
  partnerPopupTitle: 'Before you start',
  partnerPopupText:
    "We may already have your partner profile. Enter your phone number to claim it. If there's no match, continue signing up.",
  submitLabel: 'Create Partner Account',
  videoTitle: 'Welcome to NCOF Partners',
  videoUrl: '/partner-welcome.mp4',
};

const SignUpPage: React.FC<{ variant?: SignUpVariant }> = ({ variant = 'default' }) => {
  const copy = variant === 'partner' ? partnerCopy : defaultCopy;
  const isPartnerVariant = variant === 'partner';
  const { signUp, claimExistingUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as any;
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirect_to');
  const mobileRedirect = getTrustedMobileRedirect(redirectTo);
  const signupSearch = mobileRedirect
    ? `?redirect_to=${encodeURIComponent(mobileRedirect)}`
    : '';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [dob, setDob] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [phoneMatches, setPhoneMatches] = useState<PhoneMatch[]>([]);
  const [exactMatch, setExactMatch] = useState<ExactMatch | null>(null);
  const [phoneAlreadyRegistered, setPhoneAlreadyRegistered] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [showPartnerPopup, setShowPartnerPopup] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showPartnerVideoModal, setShowPartnerVideoModal] = useState(false);
  const phoneLookupRef = useRef(0);
  const toastTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    };
  }, [avatarPreview]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const storageKey = 'signup_partner_claim_popup_v1';
    if (!localStorage.getItem(storageKey)) {
      setShowPartnerPopup(true);
      localStorage.setItem(storageKey, 'shown');
    }
  }, []);

  useEffect(() => {
    if (!isPartnerVariant) return;
    const storageKey = 'partner_signup_video_modal_v1';
    if (!localStorage.getItem(storageKey)) {
      setShowPartnerVideoModal(true);
      localStorage.setItem(storageKey, 'shown');
    }
  }, [isPartnerVariant]);

  const normalizePhoneInput = (value: string) => {
    const cleaned = value.replace(/[^\d+]/g, '');
    if (!cleaned) return null;
    const asDigits = cleaned.replace(/\D/g, '');
    const candidate = cleaned.startsWith('+')
      ? cleaned
      : asDigits.length === 10
        ? `+1${asDigits}`
        : `+${asDigits}`;
    return /^\+[1-9]\d{6,14}$/.test(candidate) ? candidate : null;
  };

  const maskPhone = (value?: string | null) => {
    if (!value) return '';
    return '••• ••• ••••';
  };

  const splitName = (value?: string | null) => {
    const parts = value?.trim().split(/\s+/).filter(Boolean) ?? [];
    if (!parts.length) return { first: '', last: '' };
    if (parts.length === 1) return { first: parts[0], last: '' };
    return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
  };

  useEffect(() => {
    const trimmed = phone.trim();
    const digits = trimmed.replace(/\D/g, '');

    if (digits.length < 10) {
      setPhoneMatches([]);
      setExactMatch(null);
      setPhoneAlreadyRegistered(false);
      setCheckingPhone(false);
      return;
    }

    const requestId = ++phoneLookupRef.current;
    const timer = setTimeout(async () => {
      setCheckingPhone(true);
      try {
        const { data, error } = await supabase.functions.invoke('lookup-claimable-users', {
          body: { phone_prefix: trimmed },
        });
        if (requestId !== phoneLookupRef.current) return;
        if (error) {
          console.warn('Phone lookup failed', error);
          setPhoneMatches([]);
          setExactMatch(null);
          setPhoneAlreadyRegistered(false);
          return;
        }
        const res = data as {
          matches?: PhoneMatch[];
          exactMatch?: ExactMatch | null;
          alreadyRegistered?: boolean;
        };
        const matches = res?.matches ?? [];
        setPhoneMatches(matches);
        setExactMatch(res?.exactMatch ?? null);
        setPhoneAlreadyRegistered(Boolean(res?.alreadyRegistered));
      } finally {
        if (requestId === phoneLookupRef.current) setCheckingPhone(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [phone]);

  useEffect(() => {
    if (phoneAlreadyRegistered && currentStep > 1) {
      setCurrentStep(1);
      setError('This number is already registered. Please sign in instead.');
    }
  }, [currentStep, phoneAlreadyRegistered]);

  const showToast = (message: string) => {
    setToastMessage(message);
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(() => {
      setToastMessage(null);
    }, 3500);
  };

  const steps = [
    {
      title: 'Your Number',
      description: 'Enter your phone number to get started. ',
    },
    {
      title: 'Your name',
      description: 'Tell us who you are so we can personalize your profile.',
    },
    {
      title: 'Your details',
      description: 'Add your address, birthday, and a profile photo.',
    },
    {
      title: 'Secure it',
      description: 'Create a password to protect your account.',
    },
  ];

  const validateStep = (step: number) => {
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedPhone = phone.trim();
    const trimmedEmail = email.trim();
    const trimmedAddress = address.trim();

    if (step === 1) {
      if (!trimmedPhone || !/^\+?[0-9]{10,11}$/.test(trimmedPhone)) {
        setError('Please enter a valid 10 digit phone number.');
        return false;
      }
      if (phoneAlreadyRegistered) {
        setError('This number is already registered. Please sign in instead.');
        return false;
      }
      return true;
    }

    if (step === 2) {
      if (!trimmedFirst || !trimmedLast) {
        setError('First and last name are required.');
        return false;
      }
      if (!trimmedEmail) {
        setError('Email is required.');
        return false;
      }
      if (!/\S+@\S+\.\S+/.test(trimmedEmail)) {
        setError('Please enter a valid email address.');
        return false;
      }
      return true;
    }

    if (step === 3) {
      if (!trimmedAddress) {
        setError('Address is required.');
        return false;
      }
      if (!dob || Number.isNaN(Date.parse(dob))) {
        setError('Please enter a valid date of birth.');
        return false;
      }
      const age = calculateAge(dob);
      if (age == null) {
        setError('Please enter a valid date of birth.');
        return false;
      }
      if (age < 13) {
        setError('You must be at least 13 to create an account. Ask a parent to set up a child account for you.');
        return false;
      }
      return true;
    }

    if (step === 4) {
      if (password.length < 6) {
        setError('Password must be at least 6 characters.');
        return false;
      }
      if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
        setError('Password must contain uppercase, lowercase, and number.');
        return false;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return false;
      }
      return true;
    }

    return true;
  };

  const handleNext = () => {
    setError(null);
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(4, prev + 1));
    }
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const handleSubmit = async () => {
    setError(null);
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedPhone = phone.trim();
    const trimmedEmail = email.trim();
    const trimmedAddress = address.trim();

    if (!trimmedFirst || !trimmedLast) {
      setError('First and last name are required.');
      return;
    }
    if (!trimmedPhone || !/^\+?[0-9]{10,11}$/.test(trimmedPhone)) {
      setError('Please enter a valid US phone number.');
      return;
    }
    if (phoneAlreadyRegistered) {
      setError('This number is already registered. Please sign in instead.');
      return;
    }
    if (!trimmedEmail) {
      setError('Email is required.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!trimmedAddress) {
      setError('Address is required.');
      return;
    }
    if (!dob || Number.isNaN(Date.parse(dob))) {
      setError('Please enter a valid date of birth.');
      return;
    }
    const signupAge = calculateAge(dob);
    if (signupAge == null) {
      setError('Please enter a valid date of birth.');
      return;
    }
    if (signupAge < 13) {
      setError('You must be at least 13 to create an account. Ask a parent to set up a child account for you.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setError('Password must contain uppercase, lowercase, and number.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const fullName = `${trimmedFirst} ${trimmedLast}`.trim();
      const normalizedCurrent = normalizePhoneInput(trimmedPhone);
      const exactMatchForCurrent =
        exactMatch &&
        normalizedCurrent &&
        normalizePhoneInput(exactMatch.phone ?? '') === normalizedCurrent
          ? exactMatch
          : null;

      if (exactMatchForCurrent?.membership_status === 'member') {
        setLoading(false);
        setError('This number is already registered. Please sign in instead.');
        return;
      }

      let session = null;
      if (exactMatchForCurrent) {
        try {
          session = await claimExistingUser({
            phone: trimmedPhone,
            email: trimmedEmail || undefined,
            password,
            firstName: trimmedFirst,
            lastName: trimmedLast,
            fullName,
          });
        } catch (claimErr) {
          const msg = claimErr instanceof Error ? claimErr.message : String(claimErr);
          if (msg.includes('User not found')) {
            session = await signUp({
              phone: trimmedPhone,
              email: trimmedEmail || undefined,
              password,
              firstName: trimmedFirst,
              lastName: trimmedLast,
              fullName,
            });
          } else {
            throw claimErr;
          }
        }
      } else {
        session = await signUp({
          phone: trimmedPhone,
          email: trimmedEmail || undefined,
          password,
          firstName: trimmedFirst,
          lastName: trimmedLast,
          fullName,
        });
      }

      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      let avatarUrl: string | null = null;
      let resolvedCampus = 'Main Campus';
      if (userId) {
        const { data: existingUser } = await supabase
          .from('users')
          .select('campus')
          .eq('id', userId)
          .maybeSingle();
        if (existingUser?.campus && existingUser.campus.trim()) {
          resolvedCampus = existingUser.campus;
        }
      }
      const baseUserPayload = {
        full_name: fullName || null,
        email: trimmedEmail || null,
        phone: trimmedPhone || null,
        dob: dob || null,
        campus: resolvedCampus,
        account_kind: signupAge < 18 ? 'managed_child' : 'standard',
      };

      if (avatarFile) {
        if (avatarFile.size > 5 * 1024 * 1024) {
          throw new Error('Please upload an image smaller than 5MB.');
        }
        if (!userId) {
          throw new Error('Unable to upload avatar without an authenticated user.');
        }

        const ext = avatarFile.name.split('.').pop() || 'jpg';
        const fileName = `avatars/${userId}_${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('post-images')
          .upload(fileName, avatarFile, { upsert: true });
        if (uploadError) {
          throw new Error(uploadError.message);
        }
        const { data: publicData } = supabase.storage.from('post-images').getPublicUrl(fileName);
        avatarUrl = publicData.publicUrl;

        const { error: updateError } = await supabase
          .from('users')
          .update({ ...baseUserPayload, avatar: avatarUrl })
          .eq('id', userId);
        if (updateError) {
          await updateUserProfileWithFallback(userId, { ...baseUserPayload, avatar: avatarUrl });
        }

        // Best-effort update of auth metadata for web display
        try {
          await supabase.auth.updateUser({ data: { avatar_url: avatarUrl } });
        } catch (metadataError) {
          console.warn('Avatar metadata update failed:', metadataError);
        }
      } else if (userId) {
        await updateUserProfileWithFallback(userId, baseUserPayload);
      }

      if (userId && signupAge < 18) {
        const { error: seedError } = await supabase.rpc('seed_child_household_contacts', {
          p_child_user_id: userId,
        });
        if (seedError) {
          console.warn('seed_child_household_contacts failed during signup:', seedError.message);
        }
      }

      if (isPartnerVariant) {
        if (!userId) {
          throw new Error('Unable to submit partner request without an authenticated user.');
        }
        const { error: partnerError } = await supabase.from('partner_requests').insert({
          user_id: userId,
          name: fullName || null,
          phone: trimmedPhone || null,
          email: trimmedEmail,
          dob: dob || null,
          address: trimmedAddress || null,
          country: null,
          avatar_url: avatarUrl,
          created_at: new Date().toISOString(),
        });
        if (partnerError) {
          throw new Error(partnerError.message);
        }
      }

      if (mobileRedirect) {
        const activeSession = await waitForActiveSession(session);
        const refreshToken = activeSession?.refresh_token;

        if (!refreshToken) {
          throw new Error('Sign up succeeded but no session tokens were returned.');
        }

        // Local sign-out clears this browser only; it does not revoke the token
        // handed to the mobile app (see redirectToMobileHandoff).
        await redirectToMobileHandoff(mobileRedirect, refreshToken);
        return;
      }

      navigate('/');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStep < 4) {
      handleNext();
      return;
    }
    if (!validateStep(4)) {
      return;
    }
    await handleSubmit();
  };

  return (
    <Page>
      {toastMessage ? (
        <ToastRegion role="status" aria-live="polite">
          <Toast>{toastMessage}</Toast>
        </ToastRegion>
      ) : null}
      {showPartnerVideoModal && isPartnerVariant ? (
        <ModalOverlay onClick={() => setShowPartnerVideoModal(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{copy.videoTitle ?? 'Welcome'}</ModalTitle>
              <ModalCloseButton type="button" onClick={() => setShowPartnerVideoModal(false)}>
                ✕
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <VideoFrame>
                <video controls playsInline>
                  <source src={copy.videoUrl} />
                </video>
              </VideoFrame>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      ) : null}
      <Wrapper>
        <Hero>
          <Logo src="/ncofLogo.png" alt="New Church of Faith logo" />
          <HeroText>
            <HeroTitle>{copy.heroTitle}</HeroTitle>
            <HeroSubtitle>{copy.heroSubtitle}</HeroSubtitle>
          </HeroText>
        </Hero>
        <Stepper aria-label={copy.stepperLabel}>
          {steps.map((step, index) => {
            const stepNumber = index + 1;
            const isActive = stepNumber === currentStep;
            const isComplete = stepNumber < currentStep;
            return (
              <StepItem key={step.title}>
                <StepCircle $active={isActive} $complete={isComplete}>
                  {isComplete ? '✓' : stepNumber}
                </StepCircle>
                {step.title}
              </StepItem>
            );
          })}
        </Stepper>
        {showPartnerPopup ? (
          <PartnerPopup role="status">
            <PartnerPopupTitle>{copy.partnerPopupTitle}</PartnerPopupTitle>
            <PartnerPopupText>
              {copy.partnerPopupText}
            </PartnerPopupText>
            <PartnerPopupRow>
              <PartnerPopupButton type="button" onClick={() => setShowPartnerPopup(false)}>
                Got it
              </PartnerPopupButton>
            </PartnerPopupRow>
          </PartnerPopup>
        ) : null}
        <form onSubmit={handleFormSubmit}>
          <StepTitle>{steps[currentStep - 1].title}</StepTitle>
          <StepDescription>{steps[currentStep - 1].description}</StepDescription>

          {currentStep === 1 ? (
            <Field>
              <Label htmlFor="phone">Phone number</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="ie. 3211256547"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                required
              />
              {phoneMatches.length > 0 || checkingPhone || phoneAlreadyRegistered ? (
                <SuggestionPanel>
                  {phoneMatches.length > 0 ? (
                    <SuggestionHeader>We found matching numbers</SuggestionHeader>
                  ) : null}
                  {phoneMatches.map((match) => {
                    const normalizedInput = normalizePhoneInput(phone);
                    const normalizedMatch = normalizePhoneInput(match.phone ?? '');
                    const isExact = Boolean(normalizedInput && normalizedMatch && normalizedInput === normalizedMatch);
                    return (
                      <SuggestionRow key={match.id}>
                        <SuggestionMeta>
                          <div>{match.full_name || 'Unnamed member'}</div>
                          <SuggestionHint>{maskPhone(match.phone)}</SuggestionHint>
                        </SuggestionMeta>
                        {isExact ? (
                          <ClaimButton
                            type="button"
                            onClick={() => {
                              const { first, last } = splitName(match.full_name);
                              if (first) setFirstName(first);
                              if (last) setLastName(last);
                              if (match.phone) setPhone(match.phone);
                              showToast('Account claimed. Continue signing up.');
                            }}
                          >
                            Claim this number
                          </ClaimButton>
                        ) : (
                          <SuggestionHint>Type full number to claim</SuggestionHint>
                        )}
                      </SuggestionRow>
                    );
                  })}
                  {phoneAlreadyRegistered ? (
                    <RegisteredNotice>This number is already registered. Please sign in instead.</RegisteredNotice>
                  ) : null}
                  {checkingPhone ? <SuggestionHint>Looking up matches...</SuggestionHint> : null}
                </SuggestionPanel>
              ) : null}
            </Field>
          ) : null}

          {currentStep === 2 ? (
            <>
              <Field>
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="email">Email address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </Field>
            </>
          ) : null}

          {currentStep === 3 ? (
            <>
              <Field>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  type="text"
                  placeholder="ie. 123 Main St, Orlando, FL"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  autoComplete="street-address"
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="dob">Date of birth</Label>
                <Input
                  id="dob"
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  required
                />
                <SuggestionHint style={{ marginTop: '0.35rem' }}>
                  Users under 18 need a parent to enable social features after sign up.
                </SuggestionHint>
              </Field>
              <Field>
                <Label htmlFor="avatar">Profile photo</Label>
                <ProfilePhotoInput
                  id="avatar"
                  preview={avatarPreview}
                  onChange={(file, preview) => {
                    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
                    setAvatarFile(file);
                    setAvatarPreview(preview);
                  }}
                />
              </Field>
            </>
          ) : null}

          {currentStep === 4 ? (
            <>
              <Field>
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  variant="light"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <PasswordInput
                  id="confirmPassword"
                  variant="light"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </Field>
            </>
          ) : null}

          {currentStep === 1 ? (
            <Button type="button" onClick={handleNext} disabled={loading || phoneAlreadyRegistered}>
              Next
            </Button>
          ) : (
            <ActionRow>
              <SecondaryButton type="button" onClick={handleBack} disabled={loading}>
                Back
              </SecondaryButton>
              {currentStep < 4 ? (
                <Button type="button" onClick={handleNext} disabled={loading}>
                  Next
                </Button>
              ) : (
                <Button type="submit" disabled={loading}>
                  {loading ? 'Signing up...' : copy.submitLabel}
                </Button>
              )}
            </ActionRow>
          )}

          {error && <ErrorMsg>{error}</ErrorMsg>}
        </form>
        <p style={{ marginTop: '1rem', color: '#94a3b8' }}>
          Already have an account?{' '}
          <Link to={`/login${signupSearch}`} style={{ color: 'var(--primary)' }}>
            Login
          </Link>
        </p>
      </Wrapper>
    </Page>
  );
};

export default SignUpPage;
